import React, { useMemo } from 'react';
import { Box, IconProps } from '@landr/maestro';
import BackgroundImageFromUrl from 'components/BackgroundImageFromUrl/BackgroundImageFromUrl';
import ExclusivePackBadge from 'components/ExclusiveCardBadge/ExclusiveCardBadge';
import TakenDownBadge from 'components/TakenDownBadge/TakenDownBadge';
import IconArtwork from 'components/CommonUi/IconArtwork/IconArtwork';
import LetterArtwork from 'components/CommonUi/LetterArtwork/LetterArtwork';
import { cva } from '@landr/styled-system/css';
import { ArtworkSize } from 'types/artworkSize';
import { Pack, Repack } from 'types/generated/graphql';

export interface ArtworkProps {
    isLoading?: boolean;
    isExclusive?: boolean;
    isTakenDown?: boolean;
    isRounded?: boolean;
    imageUrl?: string;
    fallbackName?: string;
    fallbackIcon?: React.ComponentType<IconProps>;
    alt: string;
    imageSize?: ArtworkSize;
    loading?: 'lazy' | 'eager';
    entity?: Pack | Repack;
}

const artworkStyles = cva({
    base: {
        position: 'relative',
        overflow: 'hidden',
        bg: 'surface.medium',
        objectFit: 'cover',
        maxWidth: '100%',
        maxHeight: '100%',
    },
    variants: {
        size: {
            default: {
                width: '220px',
                height: '220px',
            },
            label: {
                width: '192px',
                height: '192px',
            },
            desktop: {
                width: '260px',
                height: '260px',
            },
            customHeroSlide: {
                width: '450px',
                height: '365px',
            },
            heroSlide: {
                width: '500px',
                height: '500px',
            },
        },
    },
    defaultVariants: {
        size: 'default',
    },
});

const Artwork = ({
    isLoading = false,
    isExclusive = false,
    isTakenDown = false,
    isRounded = false,
    fallbackIcon,
    fallbackName,
    imageUrl,
    alt,
    imageSize = 'default',
    loading,
}: ArtworkProps) => {
    const fallback = useMemo(() => {
        if (fallbackIcon) {
            return <IconArtwork icon={fallbackIcon} />;
        }
        if (fallbackName) {
            return <LetterArtwork name={fallbackName} />;
        }
    }, [fallbackName, fallbackIcon]);

    return (
        <Box
            data-component="Artwork"
            className={artworkStyles({ size: imageSize })}
            borderRadius={isRounded ? 'round' : 'base'}
        >
            <BackgroundImageFromUrl
                isLoading={isLoading}
                aria-label={alt}
                alt={alt}
                imageSize={imageSize}
                imageUrl={imageUrl}
                fallback={fallback}
                loading={loading}
            />
            {isExclusive && <ExclusivePackBadge isAbsolute={true} />}
            {isTakenDown && <TakenDownBadge isAbsolute={true} />}
        </Box>
    );
};

export default Artwork;
